import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

interface Props {
  src: string;
  poster: string;
  eyebrow: string;
  headline: string;
  subline: string;
}

/**
 * Vertical autoplay hero video for mobile landing pages.
 * Muted + playsInline so iOS Safari and Android Chrome will autoplay.
 * Tap-to-unmute toggle lets the visitor hear the patient story.
 */
const MobileVideoHero = ({ src, poster, eyebrow, headline, subline }: Props) => {
  const ref = useRef<HTMLVideoElement | null>(null);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    // Some mobile browsers need an explicit play() after mount.
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
  }, []);

  const toggleMute = () => {
    const v = ref.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    // Re-attempt play in case unmuting on iOS paused it.
    v.play().catch(() => {});
  };

  return (
    <section className="relative w-full bg-black overflow-hidden">
      <div className="relative w-full aspect-[9/16] max-h-[88vh]">
        <video
          ref={ref}
          src={src}
          poster={poster}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Bottom gradient for legibility */}
        <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
        {/* Top gradient for eyebrow chip */}
        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/60 to-transparent" />

        {/* Eyebrow chip */}
        <div className="absolute top-4 left-4">
          <span className="inline-block bg-white/10 backdrop-blur-sm border border-white/30 text-white text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1.5 rounded-full">
            {eyebrow}
          </span>
        </div>

        {/* Mute toggle */}
        <button
          type="button"
          onClick={toggleMute}
          aria-label={muted ? "Unmute video" : "Mute video"}
          className="absolute top-4 right-4 h-11 w-11 rounded-full bg-black/55 hover:bg-black/75 text-white border border-white/30 backdrop-blur-sm flex items-center justify-center transition-colors"
        >
          {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
        </button>

        {/* Headline pinned to bottom */}
        <div className="absolute inset-x-0 bottom-0 p-5 pb-7 text-white">
          <h1 className="!text-white font-heading text-2xl sm:text-3xl font-light leading-tight tracking-tight mb-2 drop-shadow-lg">
            {headline}
          </h1>
          <p className="text-sm sm:text-base text-white/90 leading-snug max-w-md drop-shadow-lg">
            {subline}
          </p>
        </div>
      </div>
    </section>
  );
};

export default MobileVideoHero;